(() => {
  if (customElements.get('ums-bundle-guard')) return;

  class UMSBundleGuard extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.button = this.form?.querySelector('[name="add"]');
      this.message = this.querySelector('.ums-bundle-guard__message');
      this.scope = this.closest('section[id^="MainProduct-"]');
      if (!this.form || !this.button || !this.message || !this.scope) return;
      this.parentVariants = new Set(JSON.parse(this.dataset.variantIds).map(String));
      this.button.setAttribute('aria-describedby', this.message.id);
      this.update = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      };
      this.onSubmit = (event) => this.guard(event);
      this.onClick = (event) => {
        if (event.target.closest('[name="add"]') === this.button) this.guard(event);
      };
      this.onChange = (event) => {
        if (event.target.matches?.('kaching-bundles-block') && this.scope.contains(event.target)) {
          this.removeAttribute('data-error');
          this.update();
        }
      };
      this.form.addEventListener('submit', this.onSubmit, true);
      this.form.addEventListener('click', this.onClick, true);
      document.addEventListener('variants-changed', this.onChange, true);
      document.addEventListener('deal-bar-selected', this.onChange, true);
      this.observer = new MutationObserver(this.update);
      this.observer.observe(this.scope, { childList: true, subtree: true });
      this.timer = setTimeout(() => { this.timedOut = true; this.sync(); }, 12000);
      this.sync();
    }

    state() {
      try {
        const widget = [...this.scope.querySelectorAll('kaching-bundles-block')]
          .find((node) => String(node.product?.id) === this.dataset.productId);
        if (!widget || typeof widget.isItemSelectionValid !== 'function' ||
            typeof widget.selectedVariants !== 'function' || typeof widget.pricing !== 'function' ||
            widget.dealBlock?.collectionBreaks?.requireItemSelectionEnabled !== true) {
          return { ready: false, valid: false };
        }
        const hasParts = widget.selectedVariants().some((item) =>
          Number(item.quantity) > 0 && !this.parentVariants.has(String(item.variantId)));
        const positiveTotal = Number(widget.pricing().discountedPrice?.amount) > 0;
        return { widget, ready: true, valid: widget.isItemSelectionValid() === true && hasParts && positiveTotal };
      } catch {
        return { ready: false, valid: false };
      }
    }

    sync() {
      if (!this.isConnected) return;
      const state = this.state();
      const status = state.valid ? 'complete' : state.ready ? 'incomplete' : this.timedOut ? 'unavailable' : 'loading';
      const error = this.hasAttribute('data-error');
      const key = `${status}:${error}`;
      if (key === this.lastState) return;
      this.lastState = key;
      this.dataset.state = status;
      if (!this.button.classList.contains('loading')) this.button.disabled = !state.ready;
      const label = this.button.querySelector('span');
      if (label) label.textContent = this.dataset[{
        complete: 'addButton', incomplete: 'chooseButton', unavailable: 'unavailableButton', loading: 'loadingButton'
      }[status]];
      this.message.hidden = state.valid;
      this.message.textContent = !state.ready && this.timedOut ? this.dataset.unavailable : error ? this.dataset.required : this.dataset.instruction;
    }

    guard(event) {
      const state = this.state();
      if (state.valid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setAttribute('data-error', '');
      state.widget?.validateItemSelection?.();
      this.sync();
      const target = state.widget || this.message;
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      if (state.widget) {
        state.widget.setAttribute('tabindex', '-1');
        state.widget.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.form?.removeEventListener('click', this.onClick, true);
      document.removeEventListener('variants-changed', this.onChange, true);
      document.removeEventListener('deal-bar-selected', this.onChange, true);
    }
  }

  customElements.define('ums-bundle-guard', UMSBundleGuard);
})();
